import axios from "axios";
import { toast } from "react-toastify";

const PARENT_DEVICE_STORAGE_KEY = "parent-portal-device-id";

function getOrCreateParentDeviceId() {
  if (typeof window === "undefined") {
    return "";
  }
  try {
    let id = localStorage.getItem(PARENT_DEVICE_STORAGE_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `pd-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
      localStorage.setItem(PARENT_DEVICE_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

const configuredBase = process.env.REACT_APP_BACKEND_APP_URL || "";
const localLaragonBase = "http://localhost/projects/uk/backend_tution/public/api";
const isLocalUi =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

// Local DX fallback: if configured API points to php artisan default (127.0.0.1:8000)
// but local backend is actually served by Laragon, prefer the Laragon URL.
const baseURL =
  isLocalUi && /127\.0\.0\.1:8000\/api\/?$/i.test(configuredBase)
    ? localLaragonBase
    : configuredBase || (isLocalUi ? localLaragonBase : "");

if (!process.env.REACT_APP_BACKEND_APP_URL && typeof window !== "undefined") {
  console.warn(
    "[API] REACT_APP_BACKEND_APP_URL is not set in .env. Using fallback:",
    baseURL || "(empty – set .env for production)"
  );
}

const api = axios.create({
  baseURL: baseURL || "/api",
});

// sachintha
// const api = axios.create({
//   baseURL: process.env.REACT_APP_BACKEND_APP_URL,
//   withCredentials: true,
// });

function readStoredJson(storage, key) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function normalizeToken(value) {
  if (typeof value !== "string") return null;
  const token = value.trim();
  if (!token || token === "undefined" || token === "null") return null;
  return token;
}

function resolveAuthTokens() {
  const local = typeof window !== "undefined" ? window.localStorage : null;
  const session = typeof window !== "undefined" ? window.sessionStorage : null;

  const localUserData = readStoredJson(local, "userData");
  const sessionUserData = readStoredJson(session, "userData");
  const userData = localUserData || sessionUserData || {};

  const userToken =
    normalizeToken(local?.getItem("token")) ||
    normalizeToken(session?.getItem("token"));
  const adminToken =
    normalizeToken(local?.getItem("admin-token")) ||
    normalizeToken(session?.getItem("admin-token"));
  const parentToken =
    normalizeToken(userData?.access_token) ||
    normalizeToken(userData?.data?.access_token) ||
    normalizeToken(userData?.token);

  const roleRaw = String(local?.getItem("role") || session?.getItem("role") || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

  return { userToken, adminToken, parentToken, roleRaw };
}

// Add token from localStorage to each request
api.interceptors.request.use((config) => {
  const { adminToken, userToken, parentToken, roleRaw } = resolveAuthTokens();

  const rel = String(config.url || "").replace(/^\//, "");
  const isAdminPortalRole =
    roleRaw === "admin" || roleRaw === "super admin" || roleRaw === "super_admin";

  // Default: admin panel uses admin-token when present.
  let token = adminToken || userToken || parentToken;

  // Parent / student Passport APIs must not send a leftover admin JWT (breaks scope + leaves stale RBAC).
  if (rel.startsWith("parent/")) {
    token = userToken || parentToken;
  } else if (rel.startsWith("student/")) {
    token = userToken || parentToken;
  } else if (rel.startsWith("admin/")) {
    token = adminToken || userToken || parentToken;
  } else if (rel.startsWith("tutor/")) {
    // Tutor portal should use tutor login token first; fallback keeps legacy behavior.
    token = userToken || adminToken || parentToken;
  } else if (roleRaw === "parent" || roleRaw === "student") {
    token = userToken || parentToken || adminToken;
  } else if (isAdminPortalRole) {
    token = adminToken || userToken || parentToken;
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  config.headers["X-Client-Platform"] = "web";

  // Keep /api path from baseURL when callers pass "/student/..." style URLs.
  if (typeof config.url === "string" && config.url.startsWith("/")) {
    config.url = config.url.slice(1);
  }

  if (rel.startsWith("parent/")) {
    const deviceId = getOrCreateParentDeviceId();
    if (deviceId) {
      config.headers["X-Parent-Device-Id"] = deviceId;
    }
  }

  return config;
});

// Resolve the base URL to use for proxy URLs.
// If the configured baseURL points to localhost but the browser is on a real domain,
// fall back to the current window origin + /api to avoid mixed-content / loopback CORS errors.
function getProxyBaseUrl() {
  const configured = (api.defaults.baseURL || '').replace(/\/$/, '');
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    const isRealDomain = h !== 'localhost' && h !== '127.0.0.1' && !h.startsWith('192.168.');
    if (isRealDomain && configured) {
      try {
        const u = new URL(configured);
        if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') {
          // Backend is on the same domain, served under /api via nginx proxy
          return window.location.origin + '/api';
        }
      } catch (_) { /* configured is already a path or invalid — keep as-is */ }
    }
  }
  return configured;
}

// R2 public URL for visual assets (question-bank-images, etc.). When set, images load directly from R2.
const R2_PUBLIC_BASE = (process.env.REACT_APP_R2_PUBLIC_URL || '').replace(/\/$/, '');

/**
 * Build the URL for a visual asset (path like "question-bank-images/3/xxx.jpg").
 * Uses R2 public URL when REACT_APP_R2_PUBLIC_URL is set, otherwise backend proxy.
 */
function getVisualAssetUrl(path) {
  const normalizedPath = (path || '').replace(/^\//, '');
  if (R2_PUBLIC_BASE && normalizedPath) {
    return `${R2_PUBLIC_BASE}/${normalizedPath}`;
  }
  const base = getProxyBaseUrl();
  return base ? `${base}/proxy/visual/${normalizedPath}` : normalizedPath;
}

/**
 * Origin for Laravel `/storage/...` and similar public files (not served via /api/proxy/visual).
 * Uses REACT_APP_BACKEND_APP_URL when absolute; else derives from API base URL; else window.origin.
 */
function getBackendOriginForStaticAssets() {
  const configured = (process.env.REACT_APP_BACKEND_APP_URL || '').trim().replace(/\/$/, '');
  if (configured && /^https?:\/\//i.test(configured)) {
    try {
      return new URL(configured).origin;
    } catch {
      /* ignore */
    }
  }
  const base = getProxyBaseUrl();
  if (base && /^https?:\/\//i.test(base)) {
    try {
      return new URL(base).origin;
    } catch {
      /* ignore */
    }
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
}

/**
 * Normalize img / markdown image URLs from question HTML so they load in the admin SPA
 * (root-relative /storage/... and bare question-bank paths otherwise hit the wrong host).
 */
function resolveQuestionHtmlAssetUrl(src) {
  if (src == null) return '';
  let s = String(src).trim();
  if (!s || s.startsWith('data:') || s.startsWith('blob:')) return s;

  if (s.startsWith('//')) {
    s = `${typeof window !== 'undefined' ? window.location.protocol : 'https:'}${s}`;
  }

  const visualPathFromProxy = (urlStr) => {
    try {
      const u = new URL(urlStr);
      const m = u.pathname.match(/^\/api\/proxy\/visual\/(.+)$/);
      if (m) {
        return getVisualAssetUrl(decodeURIComponent(m[1]));
      }
    } catch {
      /* ignore */
    }
    return null;
  };

  if (/^https?:\/\//i.test(s)) {
    const proxied = visualPathFromProxy(s);
    if (proxied) return proxied;
    if (/\.r2\.[^/]+\//i.test(s)) {
      try {
        const u = new URL(s);
        const path = u.pathname.replace(/^\//, '');
        if (path) return getVisualAssetUrl(path);
      } catch {
        /* ignore */
      }
    }
    return s;
  }

  if (s.startsWith('/api/proxy/visual/')) {
    const path = s.replace(/^\/api\/proxy\/visual\//, '');
    return getVisualAssetUrl(path);
  }

  if (s.startsWith('/storage/') || s.startsWith('/public/')) {
    const origin = getBackendOriginForStaticAssets();
    return origin ? `${origin}${s}` : s;
  }

  if (/^\/(?:question-bank-images|question-bank|paper-extracts|paper_images)\//i.test(s)) {
    return getVisualAssetUrl(s.replace(/^\//, ''));
  }

  if (/^(?:question-bank-images|question-bank|paper-extracts|paper_images)\//i.test(s)) {
    return getVisualAssetUrl(s.replace(/^\//, ''));
  }

  if (/^storage\//i.test(s)) {
    const origin = getBackendOriginForStaticAssets();
    return origin ? `${origin}/${s}` : `/${s}`;
  }

  return s;
}

// Rewrite R2 public URLs and stale localhost proxy URLs → R2 public URL (if set) or backend proxy.
// Handles:
//   1. Standalone R2 URL strings (entire value is an R2 URL)
//   2. Localhost proxy URLs (http://localhost:*/api/proxy/visual/...) stored in DB
//   3. R2 / localhost proxy URLs embedded inside HTML strings (img src attributes etc.)
function rewriteR2Urls(value) {
  if (typeof value === 'string') {
    // Extract path from standalone R2 URL or proxy URL for rewriting
    const r2Standalone = value.match(/^https?:\/\/[^/\s]*\.r2\.[^/\s]+\/(.+)$/);
    if (r2Standalone) {
      return getVisualAssetUrl(r2Standalone[1]);
    }

    const proxyMatch = value.match(/^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?\/api\/proxy\/visual\/(.+)$/);
    if (proxyMatch) {
      return getVisualAssetUrl(proxyMatch[1]);
    }
    // Standalone proxy URL from any host (e.g. production backend URL)
    const anyProxyMatch = value.match(/^https?:\/\/[^/\s]+\/api\/proxy\/visual\/(.+)$/);
    if (anyProxyMatch) {
      return getVisualAssetUrl(anyProxyMatch[1]);
    }

    // Replace R2 URLs embedded in HTML (e.g. inside img src="...")
    let result = value;
    result = result.replace(
      /https?:\/\/[^"'\s>]*\.r2\.[^"'\s>/]+\/([^"'\s>]+)/g,
      (_, path) => getVisualAssetUrl(path)
    );

    // Replace stale localhost proxy URLs stored in DB (any port)
    result = result.replace(
      /https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?\/api\/proxy\/visual\/([^"'\s>]+)/g,
      (_, path) => getVisualAssetUrl(path)
    );
    // Replace proxy URLs from any host (e.g. embedded in markdown/HTML from API)
    result = result.replace(
      /https?:\/\/[^/"'\s>]+\/api\/proxy\/visual\/([^"'\s>]+)/g,
      (_, path) => getVisualAssetUrl(path)
    );

    return result;
  }
  if (Array.isArray(value)) {
    return value.map(rewriteR2Urls);
  }
  if (value !== null && typeof value === 'object') {
    const result = {};
    for (const key of Object.keys(value)) {
      result[key] = rewriteR2Urls(value[key]);
    }
    return result;
  }
  return value;
}

// Prevent multiple simultaneous 401 redirects (e.g. when several parallel requests all fail at once).
let _is401Redirecting = false;

api.interceptors.response.use(
  (response) => {
    if (response.data != null && !(response.data instanceof Blob) && !(response.data instanceof ArrayBuffer)) {
      response.data = rewriteR2Urls(response.data);
    }
    return response;
  },
  (error) => {
    const skipLogoutUrls = ["student/switch-to-parent", "parent/pin/change", "student/forgot-parent-pin"];
    const isSkipUrl = skipLogoutUrls.some((url) => error.config?.url?.includes(url));

    if (error?.response?.status === 401 && !isSkipUrl && !_is401Redirecting) {
      // Testers use an Admin-scope token. API calls to student/parent/tutor endpoints
      // will return 401 — that is expected. Do not log them out; let the caller handle it.
      if ((localStorage.getItem("role") || "") === "tester") {
        return Promise.reject(error);
      }
      _is401Redirecting = true;
      const code = error?.response?.data?.code;
      if (code === "session_idle_timeout") {
        toast.info("Your session ended due to inactivity. Please log in again.");
      }
      localStorage.clear();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

/** Mock exam APIs: parents call `parent/...`, students call `student/...`. */
export function getMockExamApiPrefix() {
  const r = (localStorage.getItem("role") || "").toLowerCase().trim();
  return r === "parent" ? "parent" : "student";
}

/** Home route after exiting report/review (role-aware). */
export function getMockExamPortalHomePath() {
  const p = process.env.REACT_APP_PARENT_PREFIX || "parent";
  return getMockExamApiPrefix() === "parent" ? `/${p}/mock-exam-results` : "/student/mock-exams";
}

/** Deep link to mock exam report or review (student vs parent URL prefix). */
export function getMockExamResultPath(attemptId, segment) {
  const seg = segment === "review" || segment === "report" ? segment : "report";
  if (getMockExamApiPrefix() === "parent") {
    const pref = process.env.REACT_APP_PARENT_PREFIX || "parent";
    return `/${pref}/mock-exam/result/${attemptId}/${seg}`;
  }
  return `/student/mock-exam/result/${attemptId}/${seg}`;
}

/** Past paper APIs: parents call `parent/...`, students call `student/...`. */
export function getPastPaperApiPrefix() {
  const r = (localStorage.getItem("role") || "").toLowerCase().trim();
  return r === "parent" ? "parent" : "student";
}

export function getPastPaperPortalHomePath() {
  const p = process.env.REACT_APP_PARENT_PREFIX || "parent";
  return getPastPaperApiPrefix() === "parent" ? `/${p}/past-paper-results` : "/student/past-papers";
}

export function getPastPaperResultPath(purchaseId, segment) {
  const seg = segment === "review" || segment === "result" ? segment : "result";
  if (getPastPaperApiPrefix() === "parent") {
    const pref = process.env.REACT_APP_PARENT_PREFIX || "parent";
    return `/${pref}/past-paper/${purchaseId}/${seg}`;
  }
  return `/student/past-paper/${purchaseId}/${seg}`;
}

export { getProxyBaseUrl, getVisualAssetUrl, resolveQuestionHtmlAssetUrl };
export default api;
